import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOSchema from '../../components/feature/SEOSchema';
import { blogPosts } from '../../mocks/blogPosts';
import type { ContentSection } from '../../mocks/blogPosts';

const siteUrl = 'https://destinygpt.com';

const RELATED_CONTENT = [
  { icon: 'ri-star-smile-line', label: '점성술 분석', path: '/astrology', color: 'from-amber-500 to-orange-400' },
  { icon: 'ri-yin-yang-line', label: '사주팔자', path: '/four-pillars', color: 'from-violet-500 to-purple-600' },
  { icon: 'ri-scales-3-line', label: 'Destiny GPT', path: '/destiny-gpt', color: 'from-emerald-500 to-teal-500' },
  { icon: 'ri-moon-foggy-line', label: '꿈해몽', path: '/dream', color: 'from-sky-500 to-cyan-500' },
];

function renderSection(section: ContentSection, idx: number) {
  switch (section.type) {
    case 'heading':
      return (
        <div key={idx} className="mt-10 mb-4">
          <h2 className="text-2xl font-black text-white leading-snug">{section.heading}</h2>
          {section.body && <p className="mt-3 text-purple-200/80 leading-relaxed text-base">{section.body}</p>}
        </div>
      );
    case 'subheading':
      return (
        <div key={idx} className="mt-7 mb-3">
          <h3 className="text-lg font-bold text-primary-gold/90 leading-snug">{section.heading}</h3>
          {section.body && <p className="mt-2 text-purple-200/80 leading-relaxed text-base">{section.body}</p>}
        </div>
      );
    case 'paragraph':
      return (
        <p key={idx} className="text-purple-200/80 leading-relaxed text-base">{section.body}</p>
      );
    case 'list':
      return (
        <div key={idx} className="mt-6">
          {section.heading && <p className="text-white font-bold mb-3 text-base">{section.heading}</p>}
          <ul className="space-y-2">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-purple-200/80 text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-gold flex-shrink-0"></span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'callout':
      return (
        <div key={idx} className="my-7 rounded-2xl bg-primary-gold/8 border border-primary-gold/25 px-6 py-5">
          {section.heading && (
            <p className="text-primary-gold font-bold text-sm mb-2 flex items-center gap-2">
              <i className="ri-sparkles-line"></i>
              {section.heading}
            </p>
          )}
          <p className="text-white/90 text-base leading-relaxed">{section.body}</p>
        </div>
      );
    case 'tip':
      return (
        <div key={idx} className="my-6 rounded-2xl bg-emerald-500/8 border border-emerald-500/25 px-6 py-5">
          <p className="text-emerald-400 font-bold text-sm mb-2 flex items-center gap-2">
            <i className="ri-lightbulb-line"></i>
            {section.heading || '팁'}
          </p>
          <p className="text-white/80 text-base leading-relaxed">{section.body}</p>
        </div>
      );
    case 'warning':
      return (
        <div key={idx} className="my-6 rounded-2xl bg-amber-500/8 border border-amber-500/25 px-6 py-5">
          <p className="text-amber-400 font-bold text-sm mb-2 flex items-center gap-2">
            <i className="ri-alert-line"></i>
            {section.heading || '주의'}
          </p>
          <p className="text-white/80 text-base leading-relaxed">{section.body}</p>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#06030f] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">포스트를 찾을 수 없습니다</h1>
          <Link to="/blog" className="text-primary-gold hover:underline cursor-pointer">블로그로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const postSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${siteUrl}/blog/${post.slug}`,
    headline: post.title,
    description: post.excerpt,
    url: `${siteUrl}/blog/${post.slug}`,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: { '@type': 'Organization', name: post.author, url: siteUrl },
    publisher: { '@type': 'Organization', name: '데스티니GPT', url: siteUrl },
    image: post.imageUrl,
    keywords: post.tags.join(', '),
    inLanguage: 'ko-KR',
    isPartOf: { '@type': 'Blog', url: `${siteUrl}/blog`, name: '데스티니AI 포춘 블로그' },
    about: { '@type': 'Thing', name: '점성술 사주 운세 데스티니AI 포춘' },
  };

  const relatedPosts = blogPosts.filter((p) => p.id !== post.id && p.category === post.category).slice(0, 3);
  const otherPosts = blogPosts.filter((p) => p.id !== post.id && p.category !== post.category).slice(0, 3 - relatedPosts.length);
  const suggestedPosts = [...relatedPosts, ...otherPosts].slice(0, 3);

  return (
    <div className="min-h-screen bg-[#06030f] text-white">
      <SEOSchema schema={postSchema} id="blog-post-schema" />
      <Navbar />

      {/* ── HERO IMAGE ── */}
      <div className="relative h-72 sm:h-96 mt-16 sm:mt-20 overflow-hidden">
        <img
          src={post.imageUrl}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06030f]/40 via-transparent to-[#06030f]" />
      </div>

      {/* ── ARTICLE ── */}
      <article className="max-w-3xl mx-auto px-4 pb-20 -mt-8 relative z-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-purple-400/60 mb-6">
          <Link to="/" className="hover:text-primary-gold transition-colors cursor-pointer">홈</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/blog" className="hover:text-primary-gold transition-colors cursor-pointer">블로그</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-purple-300/60 line-clamp-1">{post.title}</span>
        </nav>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="px-3 py-1 rounded-full bg-primary-gold/15 border border-primary-gold/30 text-primary-gold text-xs font-semibold">
            {post.category}
          </span>
          <span className="text-purple-400/50 text-xs">{post.publishedAt}</span>
          <span className="text-purple-400/50 text-xs">·</span>
          <span className="text-purple-400/50 text-xs">읽기 {post.readingTime}분</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-3 leading-tight">{post.title}</h1>
        <p className="text-xl text-primary-gold/80 font-medium mb-6 leading-snug">{post.subtitle}</p>

        {/* Author */}
        <div className="flex items-center gap-3 mb-8 pb-8 border-b border-white/8">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-primary-gold/30 to-amber-600/20 border border-primary-gold/40">
            <i className="ri-user-star-line text-primary-gold text-base"></i>
          </div>
          <div>
            <p className="text-white text-sm font-semibold">{post.author}</p>
            <p className="text-purple-400/60 text-xs">데스티니AI 포춘 공식 블로그</p>
          </div>
        </div>

        {/* Excerpt callout */}
        <div className="rounded-2xl bg-primary-gold/5 border-l-4 border-primary-gold px-6 py-5 mb-10">
          <p className="text-white/90 text-base leading-relaxed font-medium">{post.excerpt}</p>
        </div>

        {/* Article Body */}
        <div className="prose prose-invert max-w-none mt-6">
          {post.contentSections && post.contentSections.length > 0 ? (
            <div className="space-y-2">
              {post.contentSections.map((section, idx) => renderSection(section, idx))}
            </div>
          ) : (
            <div className="space-y-5 text-purple-200/80 leading-relaxed">
              <p className="text-base">{post.content}</p>
              <h2 className="text-2xl font-black text-white mt-10 mb-4">
                데스티니AI 포춘으로 바로 확인하는 법
              </h2>
              <p>
                데스티니AI 포춘은 이 모든 분석을 AI 기술로 자동화합니다. 복잡한 전통 운세 체계를 직접
                공부하지 않아도, 생년월일·출생 시간만 입력하면 AI가 즉시 당신만의 운세 리포트를 생성합니다.
              </p>
            </div>
          )}

          {/* Always show closing section */}
          <div className="mt-10 space-y-4 text-purple-200/80 leading-relaxed">
            <h2 className="text-2xl font-black text-white mb-4">
              Destiny GPT 결정 비서와 함께하기
            </h2>
            <p>
              단순한 운세 확인을 넘어, <strong className="text-white">Destiny GPT 결정 비서</strong>는
              직장 전환·결혼·이사·창업 등 중대한 결정의 최적 시기를 사주 오행으로 분석해드립니다.
              데스티니AI 포춘의 97% 사용자 만족도는 이러한 실질적인 활용 가능성에서 비롯됩니다.
            </p>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-white/8">
          {post.tags.map((tag) => (
            <Link
              key={tag}
              to={`/blog?q=${encodeURIComponent(tag)}`}
              className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-purple-300/60 text-xs hover:border-primary-gold/30 hover:text-primary-gold transition-all cursor-pointer"
            >
              #{tag}
            </Link>
          ))}
        </div>

        {/* CTA box */}
        <div className="mt-12 rounded-2xl border border-primary-gold/20 bg-gradient-to-br from-primary-gold/5 to-violet-900/10 p-8 text-center">
          <i className="ri-sparkles-line text-primary-gold text-3xl mb-3 block"></i>
          <h2 className="text-2xl font-black text-white mb-2">
            지금 <span className="text-primary-gold">데스티니AI 포춘</span> 무료 체험
          </h2>
          <p className="text-purple-300/60 text-sm mb-5">생년월일을 입력하면 AI가 즉시 당신의 운명을 분석합니다.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-primary-gold to-amber-400 text-black font-bold rounded-xl hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
          >
            <i className="ri-flashlight-line"></i>
            무료 운세 분석 시작
          </Link>
        </div>
      </article>

      {/* ── QUICK SERVICE LINKS ── */}
      <section className="max-w-3xl mx-auto px-4 mb-16">
        <h2 className="text-lg font-bold text-white mb-4">바로가기 서비스</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {RELATED_CONTENT.map((rc) => (
            <Link
              key={rc.label}
              to={rc.path}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl border border-white/8 bg-white/2 hover:border-primary-gold/20 hover:bg-white/5 transition-all cursor-pointer group"
            >
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${rc.color}`}>
                <i className={`${rc.icon} text-white text-base`}></i>
              </div>
              <span className="text-white/70 text-xs font-medium group-hover:text-white transition-colors text-center">{rc.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── RELATED POSTS ── */}
      {suggestedPosts.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 mb-20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white">관련 아티클</h2>
            <Link to="/blog" className="text-primary-gold/70 text-xs hover:text-primary-gold transition-colors cursor-pointer">
              전체 보기 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="space-y-3">
            {suggestedPosts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="flex gap-4 p-4 rounded-xl border border-white/8 bg-white/2 hover:border-primary-gold/20 hover:bg-white/4 transition-all cursor-pointer group"
              >
                <img
                  src={p.imageUrl}
                  alt={p.title}
                  className="w-20 h-16 rounded-lg object-cover object-top flex-shrink-0"
                />
                <div>
                  <p className="text-primary-gold/60 text-[10px] uppercase font-semibold tracking-widest mb-1">{p.category}</p>
                  <h3 className="text-white text-sm font-bold group-hover:text-primary-gold transition-colors line-clamp-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-purple-400/50 text-xs mt-1">{p.publishedAt} · {p.readingTime}분</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
